/**
 * Pure helpers for shaping the conversation history that ships to Claude.
 *
 * Lives outside App.js so tests can import them without dragging in the
 * full React tree (react-markdown is ESM-only and chokes jest's CJS
 * loader otherwise).
 */

/**
 * Fingerprint a validation result so we can detect "auto-fix made zero
 * progress" — the next validation looks identical to the one we just
 * fixed against. Combines the classifier's category with the sorted
 * issues list — if both match, we're looping and should stop calling
 * Claude. Exact same output rules as before; extracted from App.js.
 */
export function computeValidationSignature(validation) {
  if (!validation) return "";
  const category = validation.diagnosis?.category || "no-diagnosis";
  const issues = (validation.issues || []).slice().sort().join(" || ");
  // Include runtime error fingerprint too — classifier may not always
  // differentiate e.g. a 502 from a 500, but the raw error text will.
  const errorSigs = (validation.runtimeErrors || [])
    .map((e) => `${e.status || ""}:${(e.message || "").slice(0, 80)}`)
    .sort()
    .join(" || ");
  return `${category}::${issues}::${errorSigs}`;
}

/**
 * Build the conversation history that actually ships to Claude.
 *
 * Two concerns here:
 *
 * 1. Revert waterline. When the user rolls back to a previous snapshot we
 *    drop a `_isRevertMarker` message into the UI log. From THAT point
 *    forward, Claude should see only post-revert turns — otherwise the
 *    pre-revert edits get re-injected into context and Claude happily
 *    "re-implements" the features the user just reverted.
 *
 * 2. Context budget. For apps deep into an iteration cycle, a 30-turn
 *    history blows past Claude's useful context window. We pin the FIRST
 *    user+assistant exchange (original intent, e.g. "build a weather
 *    dashboard") and keep the LAST 8 turns for short-term context.
 *    Everything in between gets dropped — it's already reflected in the
 *    current app code anyway.
 */
export function buildHistoryWithWaterline(messages) {
  if (!Array.isArray(messages)) return [];

  // Slice off anything at-or-before the most recent revert marker.
  let lastRevertIdx = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i]._isRevertMarker) { lastRevertIdx = i; break; }
  }
  const postRevert = lastRevertIdx >= 0 ? messages.slice(lastRevertIdx + 1) : messages;

  // Only user/assistant turns go to Claude; system messages are UI-only.
  const filtered = postRevert.filter((m) => m.role === "user" || m.role === "assistant");

  if (filtered.length > 10) {
    const pinned = filtered.slice(0, 2);   // original intent
    const recent = filtered.slice(-8);     // short-term context
    return [...pinned, ...recent].map((m) => ({ role: m.role, content: m.content }));
  }
  return filtered.slice(-10).map((m) => ({ role: m.role, content: m.content }));
}
